import { useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { fetchAdminExport } from '../../lib/apiClient';

/**
 * The **Export Emails** control (spec §3, §13).
 *
 * One export control. Production's action bar carries four of them, and this
 * renders any one: the label and the path come from `ADMIN_EXPORTS`.
 *
 * **The labels are production's, including their mismatches**: "Export Emails"
 * downloads `users.csv`, and "Export Bottleneck Answers" downloads
 * `bottleneck-assessment-answers.csv`. The specification lists the first as a
 * cosmetic defect with no decision taken, and an undecided one is reproduced.
 *
 * **A button rather than a link, and that is forced.** Production's control is
 * a plain link because its session travels in a cookie; V2 authenticates with a
 * bearer token, which a link cannot carry. The endpoint, the filename, and the
 * bytes are identical.
 */
export function ExportControl({ label, path }: { label: string; path: string }) {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [failed, setFailed] = useState(false);

  async function download() {
    if (!token) return;
    setFailed(false);

    const res = await fetchAdminExport(token, path);
    if (res.state !== 'ok') {
      // Stated rather than silent. A download that produces no file and says
      // nothing is indistinguishable from a browser that blocked it.
      setFailed(true);
      return;
    }

    const url = URL.createObjectURL(new Blob([res.body], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = res.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => void download()}
        data-testid="admin-export"
        data-export={path}
        className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {label}
      </button>

      {failed ? (
        <p className="text-xs font-medium" data-testid="admin-export-error" role="status">
          Error loading admin data
        </p>
      ) : null}
    </div>
  );
}
